import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useFilteredSocketVehicles } from "../../../../store/admin/vehiclesSocketSelector";
// Con la extensión, a propósito: en esta carpeta conviven UnitsTable.tsx y
// unitsTable.ts, que sólo difieren en mayúsculas. En Linux (CI) son dos archivos
// distintos; en un disco que no distingue mayúsculas (Windows, macOS) el
// especificador sin extensión resuelve `./UnitsTable` a unitsTable.ts y el build
// revienta con `"default" is not exported` — medido el 13-oct-2026 al desplegar
// en local con GitHub Actions caído.
import UnitsTable from "./UnitsTable.tsx";
import { clampTableHeight, defaultTableHeight } from "./unitsTable.ts";
import { mapChrome, mapChromeBase } from "../../../../components/ui/mapChrome";

const OPEN_KEY = "dumax.lite.unitsTable.open";
const HEIGHT_KEY = "dumax.lite.unitsTable.height";

const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* sin almacenamiento: vale para esta sesión */
  }
};

interface Props {
  isDarkMode: boolean;
  /** Sólo escritorio: en el teléfono no hay alto para mapa + tabla. */
  enabled: boolean;
  /** El mapa. La tabla va DEBAJO y no encima: el mapa se encoge y sus
   * controles (recentrar, leyenda, logo de Google) se reacomodan solos en
   * vez de quedar tapados. */
  children: ReactNode;
}

// La estructura (área + mapa) es la misma con o sin tabla: si cambiara al
// cruzar el ancho de móvil, React volvería a montar el mapa.
export default function UnitsTableDock({ isDarkMode, enabled, children }: Props) {
  const { t } = useTranslation("lite");
  const count = useFilteredSocketVehicles().length;
  const areaRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(() => read(OPEN_KEY) === "1");
  const [height, setHeight] = useState<number | null>(() => {
    const h = Number(read(HEIGHT_KEY));
    return Number.isFinite(h) && h > 0 ? h : null;
  });
  const [area, setArea] = useState(0);

  useEffect(() => {
    const el = areaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setArea(el.clientHeight));
    ro.observe(el);
    setArea(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  const toggle = (next: boolean) => {
    setOpen(next);
    write(OPEN_KEY, next ? "1" : "0");
  };

  const h = area > 0 ? clampTableHeight(height ?? defaultTableHeight(area), area) : 0;
  const setAndSave = useCallback(
    (px: number) => {
      const v = clampTableHeight(px, area);
      setHeight(v);
      write(HEIGHT_KEY, String(v));
    },
    [area],
  );

  // Arrastre del borde: sigue al puntero 1:1 desde donde se agarró (no salta
  // al centro del borde) y guarda al soltar.
  const drag = useRef<{ y: number; h: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { y: e.clientY, h };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setHeight(clampTableHeight(drag.current.h + (drag.current.y - e.clientY), area));
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    drag.current = null;
    setAndSave(h);
  };

  return (
    <div ref={areaRef} className="h-full flex flex-col min-h-0">
      <div className="relative flex-1 min-h-0">
        {children}
        {enabled && !open && (
          // Abajo al centro: lejos del logo de Google (abajo izquierda) y del
          // recentrar (abajo derecha).
          <button
            type="button"
            onClick={() => toggle(true)}
            className={`absolute bottom-8 left-1/2 -translate-x-1/2 z-10 h-8 px-3 flex items-center gap-2 rounded-[10px] text-[13px] font-medium transition-[background-color,transform] duration-150 ease-out active:scale-[0.97] ${mapChromeBase} ${mapChrome(isDarkMode).surface} ${mapChrome(isDarkMode).solidHover}`}
          >
            <i className={`ri-table-line text-[15px] ${mapChrome(isDarkMode).muted}`} aria-hidden />
            {t("unitsTable.open")}
            <span className={`text-xs tabular-nums ${mapChrome(isDarkMode).muted}`}>{count}</span>
          </button>
        )}
      </div>

      {enabled && open && area > 0 && (
        <div className="relative shrink-0 motion-safe:animate-fadeIn" style={{ height: h }}>
          {/* Borde para cambiar el alto. Área de agarre de 10 px sobre una línea
              de 1 px: fácil de atinar sin verse pesado. Con teclado, flechas. */}
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label={t("unitsTable.resize")}
            aria-valuenow={h}
            tabIndex={0}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onKeyDown={(e) => {
              if (e.key === "ArrowUp") setAndSave(h + 24);
              if (e.key === "ArrowDown") setAndSave(h - 24);
            }}
            className="group absolute -top-[5px] left-0 right-0 h-[10px] z-20 cursor-row-resize touch-none outline-none flex items-center justify-center"
          >
            <span
              className={`h-1 w-10 rounded-full transition-colors ${
                isDarkMode ? "bg-gray-600 group-hover:bg-gray-400" : "bg-gray-300 group-hover:bg-gray-400"
              } group-focus-visible:bg-primary-500`}
            />
          </div>
          <UnitsTable isDarkMode={isDarkMode} onClose={() => toggle(false)} />
        </div>
      )}
    </div>
  );
}
